/**
 * Some keys have underscores because values come from API
 *
 * POIs:
 * - From Schools POIs
 * -- high_schools & primary_schools
 *
 * - From Enhanced POIs
 * -- alcohol, clothing, common_needs, conference_centers, entertainment, hospitals, libraries, pharmacies, retail, shops_and_services
 */

const namespace = {
  attribution: "Powered by",
  time: {
    minute_one: "Minute",
    minute_other: "Minutes",
    hour_one: "Hour",
    hour_other: "Hours",
  },
  distance: {
    meter: "m",
    kilometer: "km",
    yard: "yd",
    mile: "mi",
  },
  language: {
    english: "English",
    french: "French",
    spanish: "Spanish",
  },
  map: {
    marker: {
      schoolAttributes: {
        levels: "Level",
        grades: "Grades",
        languages: "Languages",
        types: "Type",
        board: "Board",
        programs: "Programs",
        categories: "Categories",
        rating: "School rating",
      },
      schoolCta: "Link to school",
      district: "District",
      categories: {
        advancedPlacement: "advanced placement",
        charter: "charter",
        magnet: "magnet",
      },
    },
    cluster: {
      poiCount_one: "{{count}} Location",
      poiCount_other: "{{count}} Locations",
    },
  },
  scores: {
    cafes: {
      title: "Cafes",
      description: "A measure of the quantity of nearby coffee shops.",
    },
    car_friendly: {
      title: "Car friendly",
      description:
        "Represents how well traffic flows, and how easy it is to park.",
    },
    cycling_friendly: {
      title: "Cycling friendly",
      description:
        "Represents how suitable the area is for bicycle commuting or recreational cycling, considering nearby bike paths and lanes, the percentage of cycling commuters, the flatness of the terrain, and the presence of bike-sharing systems.",
    },
    daycares: {
      title: "Daycares",
      description: "Represents the distance to the nearest daycare.",
    },
    greenery: {
      title: "Greenery",
      description: "Quantity of nearby green spaces and tree canopy coverage.",
    },
    groceries: {
      title: "Groceries",
      description: "A measure of the quantity of nearby grocery stores.",
    },
    high_schools: {
      title: "High schools",
      description: "Represents the distance to nearest high school.",
    },
    historic: {
      title: "Historic",
      description:
        "A measure of the quantity and size of heritage buildings, and the average building age in the area.",
    },
    nightlife: {
      title: "Nightlife",
      description: "Represents the quantity of bars nearby.",
    },
    parks: {
      title: "Parks",
      description: "A measure of the quantity and size of nearby parks.",
    },
    pedestrian_friendly: {
      title: "Pedestrian friendly",
      description:
        "A measure of how many basic services can be reached on foot and how pleasant it is to walk to them.",
    },
    primary_schools: {
      title: "Primary schools",
      description:
        "A measure of the distance to the nearest elementary school.",
    },
    quiet: {
      title: "Quiet",
      description: "A measure of the average levels of noise nearby.",
    },
    restaurants: {
      title: "Restaurants",
      description: "Represents the quantity of restaurants nearby.",
    },
    shopping: {
      title: "Shopping",
      description:
        "A measure of the quantity of fashion and department stores nearby.",
    },
    transit_friendly: {
      title: "Transit friendly",
      description:
        "A measure of the frequency and level of public transit service.",
    },
    vibrant: {
      title: "Vibrancy",
      description: "Represents the level of street activity nearby.",
    },
    wellness: {
      title: "Wellness",
      description:
        "Represents how conducive a location is to individuals' well-being.",
    },
  },
  pois: {
    airports: "Airports",
    alcohol: "Alcohol",
    art_culture_science: "Art, culture, science",
    banks: "Banks",
    bus_stops: "Bus",
    cafes: "Coffee shops",
    car_fueling_stations: "Gas stations",
    casino: "Casino",
    clothing: "Clothing",
    common_needs: "Common needs",
    conference_centers: "Conference centers",
    convenience_stores: "Convenience stores",
    daycares: "Daycares",
    department_stores: "Department stores",
    entertainment: "Entertainment",
    fitness: "Fitness",
    groceries: "Groceries",
    groceries_specialized: "Specialty",
    health_care: "Health care",
    home_improvement: "Home improvement",
    hospitals: "Hospitals",
    libraries: "Libraries",
    nightlife: "Nightlife",
    non_food_shops: "Shopping",
    parks: "Parks",
    personal_care: "Personal care",
    pet_care: "Pet care",
    pharmacies: "Pharmacies",
    rapid_transit_stations: "Rapid transit",
    restaurants: "Restaurants",
    retail: "Retail",
    shops_and_services: "Shops and services",
    sport_facilities: "Sport facilities",
    train_stations: "Train",
    // Schools - from Schools API
    high_schools: "High schools",
    primary_schools: "Primary schools",
    // Deprecated
    healthy_restaurants: "Healthy restaurants",
    shopping: "Shopping",
  },
  valueDrivers: {
    bus_service_access: "Bus services",
    shops_and_services_access: "Shops and Services",
    coffee_shop_access: "Coffee shops",
    clothing_and_accessory_store_access: "Clothing stores",
    common_needs_access: "Common necessities",
    department_store_access: "Department stores",
    entertainment_venue_access: "Entertainment venues",
    fitness_establishment_access: "Fitness",
    nightlife_access: "Nightlife",
    pedestrian_oriented_street_access: "Walkable streets",
    rapid_transit_service_access: "Rapid transit service",
    restaurant_access: "Restaurants",
    specialty_grocery_store_access: "Specialty groceries",
    sports_facility_access: "Sport facilities",
    train_service_access: "Train stations",
    nearest_highway_proximity: "Highway access",
    nearest_pharmacy_proximity: "Nearby pharmacies",
    nearest_bicycle_share_station_proximity: "Nearby bikeshare stations",
    bicycle_path_access: "Bike path access",
    bicycle_share_station_access: "Bike share",
    nearest_park_area: "Nearby large park",
    bicycle_infrastructure_quality: "Bike infrastructure",
    high_schools: "High schools",
    primary_schools: "Elementary schools",
    quiet: "Quiet",
    daycares: "Daycares",
    parks: "Parks",
  },
} as const;

export default namespace;
